import { useState, useEffect, useRef } from 'react';
import { useRestaurant } from '../hooks/useMenu';
import { getAvatarColor } from './RestaurantHero';
import { hapticFeedback, isFullscreenExpected, onFullscreenChange } from '../lib/telegram';

interface SafeAreaHeaderProps {
  restaurantId: string | null;
  onPillClick?: () => void;
}

/** Delay (ms) before revealing the restaurant name inside the pill. */
const NAME_REVEAL_DELAY = 3000;

/**
 * Fixed header that fills the safe-area gap at the top of the screen in fullscreen mode.
 * Displays a Telegram-style centered pill with the business logo and name.
 *
 * Animation: the pill starts showing only the logo/initial, then after 3 seconds
 * the name is revealed with a smooth width + opacity transition.
 *
 * The height is driven entirely by the CSS variable --safe-area-top which is set
 * by telegram.ts. On page refresh, telegram.ts restores persisted values from
 * sessionStorage synchronously at module load (before React renders), so the
 * CSS vars always have correct values from the very first frame.
 */
export function SafeAreaHeader({ restaurantId, onPillClick }: SafeAreaHeaderProps) {
  const { data: restaurant } = useRestaurant(restaurantId);
  const [logoFailed, setLogoFailed] = useState(false);
  const [showName, setShowName] = useState(false);
  const [isPressed, setIsPressed] = useState(false);
  const pressTimeout = useRef<ReturnType<typeof setTimeout>>();
  const [isFullscreen, setIsFullscreen] = useState(isFullscreenExpected);

  // Subscribe to fullscreen changes so the header appears/disappears dynamically
  useEffect(() => {
    return onFullscreenChange(setIsFullscreen);
  }, []);

  // Cleanup timeout on unmount
  useEffect(() => {
    return () => clearTimeout(pressTimeout.current);
  }, []);

  const name = restaurant?.name ?? '';

  // Reveal the name after a delay
  useEffect(() => {
    if (!name) return;
    const timer = setTimeout(() => setShowName(true), NAME_REVEAL_DELAY);
    return () => clearTimeout(timer);
  }, [name]);

  // Don't render anything in non-fullscreen mode (desktop, older TG clients)
  // because --safe-area-top is 0 and the header would show an incorrect gap.
  // This guard is placed AFTER all hooks to satisfy React's rules of hooks.
  if (!isFullscreen) return null;

  const handlePillClick = () => {
    if (!onPillClick) return;
    hapticFeedback('impact', 'medium');
    onPillClick();
  };

  // Reset pressed state after a short delay for visual feedback
  const handleTouchStart = () => {
    clearTimeout(pressTimeout.current);
    setIsPressed(true);
  };

  const handleTouchEnd = () => {
    pressTimeout.current = setTimeout(() => setIsPressed(false), 150);
  };

  const logoUrl = restaurant?.logoUrl;
  const showLogo = !!logoUrl && !logoFailed;
  const initial = name.charAt(0).toUpperCase() || '?';
  const truncatedName = name.length > 17 ? `${name.slice(0, 17)}…` : name;

  return (
    <div
      className="fixed top-0 left-0 right-0 z-[60] flex items-end justify-center"
      style={{
        height: 'var(--safe-area-top, 0px)',
        background: 'var(--tg-theme-bg-color, #fff)',
        paddingBottom: '7px',
      }}
    >
      {/* Only render the pill when we have restaurant data */}
      {name && (
        <div
          className="flex items-center rounded-full bg-tg-secondary-bg max-w-[70%] cursor-pointer select-none"
          style={{
            padding: showName ? '4px 8px 4px 4px' : '4px',
            gap: showName ? '8px' : '0px',
            transform: isPressed ? 'scale(1.12)' : 'scale(1)',
            transition: 'padding 0.4s ease, gap 0.4s ease, transform 0.15s ease',
          }}
          onClick={handlePillClick}
          onTouchStart={handleTouchStart}
          onTouchEnd={handleTouchEnd}
          onMouseDown={handleTouchStart}
          onMouseUp={handleTouchEnd}
          onMouseLeave={() => setIsPressed(false)}
          role={onPillClick ? 'button' : undefined}
          tabIndex={onPillClick ? 0 : undefined}
        >
          {/* Logo - inside pill with subtle ring */}
          <div
            className="w-[21px] h-[21px] rounded-full overflow-hidden shrink-0 flex items-center justify-center ring-1 ring-white/30"
            style={showLogo ? { backgroundColor: '#fff' } : { backgroundColor: getAvatarColor(name) }}
          >
            {showLogo ? (
              <img
                src={logoUrl!}
                alt=""
                className="w-full h-full object-cover"
                onError={() => setLogoFailed(true)}
              />
            ) : (
              <span className="text-[10px] font-bold text-white leading-none">
                {initial}
              </span>
            )}
          </div>
          {/* Name — revealed after delay with width + opacity animation */}
          <span
            className="text-[12px] font-semibold text-tg-text leading-tight whitespace-nowrap overflow-hidden"
            style={{
              maxWidth: showName ? '200px' : '0px',
              opacity: showName ? 1 : 0,
              transition: 'max-width 0.4s ease, opacity 0.3s ease 0.1s',
            }}
          >
            {truncatedName}
          </span>
        </div>
      )}
    </div>
  );
}

